import React from 'react';
import { Globe } from 'lucide-react';
import T from 'Components/Helpers/translate';
import type { ProspectingCoveragePanel } from './types';

/**
 * What the assistant could not answer, and why, before anything is done about it.
 *
 * Structured search covers 13 markets and the filters in the registry: instant
 * and free. Everything else needs web research, which is slower and spends
 * credits. This card exists so neither half is decided silently - dropping the
 * uncovered part reads to a rep as "there are none", and spending credits without
 * being asked is worse.
 *
 * There is no confirm button. Running the web research needs an ICPHypothesis,
 * which today only the seller-context strategies produce - there is no path from
 * a rep's filters to one. A button that cannot do anything is worse than an
 * honest explanation, so the card offers the covered alternative instead.
 */
const REASON_KEY: Record<string, string> = {
	country: 'prospecting.v2.coverage.reasonCountry',
	noSuchFilter: 'prospecting.v2.coverage.reasonNoFilter'
};

const CoverageCard = ({ panel, onAsk }: { panel: ProspectingCoveragePanel; onAsk?: (text: string) => void }) => {
	if (!panel.outOfCoverage?.length) return null;

	return (
		<div className="overflow-hidden rounded-lg border border-border border-l-[3px] border-l-bright-orange bg-background">
			<div className="flex items-center gap-2 bg-grey-1 px-4 py-2.5">
				<Globe size={14} className="text-grey-10" />
				<span className="text-body-sm font-medium text-foreground">{T('prospecting.v2.coverage.title')}</span>
			</div>

			<ul className="flex flex-col gap-2.5 px-4 py-3">
				{panel.outOfCoverage.map(item => (
					<li key={item.ask} className="flex flex-col gap-0.5">
						<div className="text-body-sm text-foreground">{item.ask}</div>
						<div className="text-body-sm text-muted-foreground">
							{T(REASON_KEY[item.reason] ?? 'prospecting.v2.coverage.reasonNoFilter')}
						</div>
					</li>
				))}
			</ul>

			<div className="border-t border-border/60 px-4 py-2.5">
				<div className="text-body-sm text-muted-foreground">
					{T('prospecting.v2.coverage.covered', {
						markets: panel.covered?.countries?.length ?? 0,
						filters: panel.covered?.filterCount ?? 0
					})}
				</div>
				{panel.instantAlternative && onAsk ? (
					<button
						type="button"
						onClick={() => onAsk(panel.instantAlternative as string)}
						className="mt-1.5 rounded-full border border-border bg-grey-1 px-3 py-1 text-body-sm text-grey-11 hover:border-grey-6 hover:text-foreground"
					>
						{panel.instantAlternative}
					</button>
				) : null}
			</div>
		</div>
	);
};

export default CoverageCard;
